import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, FileText, TrendingUp, Clock, Eye } from 'lucide-react';
import { adminApi } from '@/services/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await adminApi.getDashboard();
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    nueva: 'bg-blue-100 text-blue-800',
    contactada: 'bg-yellow-100 text-yellow-800',
    cotizada: 'bg-green-100 text-green-800',
    cerrada: 'bg-gray-100 text-gray-800'
  };

  const statusLabels = {
    nueva: 'Nueva',
    contactada: 'Contactada',
    cotizada: 'Cotizada',
    cerrada: 'Cerrada'
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="font-heading text-2xl font-bold text-steel-dark uppercase tracking-wider mb-6">
        Dashboard
      </h1>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-steel-gray text-sm">Total Productos</p>
              <p className="font-heading text-3xl font-bold text-steel-dark">{data?.total_products || 0}</p>
            </div>
            <div className="w-12 h-12 bg-accent-primary/10 rounded-full flex items-center justify-center">
              <Package className="text-accent-primary" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-steel-gray text-sm">Cotizaciones Totales</p>
              <p className="font-heading text-3xl font-bold text-steel-dark">{data?.total_quotes || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <FileText className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-steel-gray text-sm">Cotizaciones Nuevas</p>
              <p className="font-heading text-3xl font-bold text-accent-primary">{data?.new_quotes || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <TrendingUp className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-steel-gray text-sm">Este Mes</p>
              <p className="font-heading text-3xl font-bold text-steel-dark">{data?.quotes_month || 0}</p>
            </div>
            <div className="w-12 h-12 bg-accent-warm/10 rounded-full flex items-center justify-center">
              <Clock className="text-accent-warm" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded shadow p-4">
          <h3 className="font-semibold text-steel-dark mb-2">Hoy</h3>
          <p className="text-2xl font-bold text-accent-primary">{data?.quotes_today || 0} cotizaciones</p>
        </div>
        <div className="bg-white rounded shadow p-4">
          <h3 className="font-semibold text-steel-dark mb-2">Esta Semana</h3>
          <p className="text-2xl font-bold text-accent-warm">{data?.quotes_week || 0} cotizaciones</p>
        </div>
        <div className="bg-white rounded shadow p-4">
          <h3 className="font-semibold text-steel-dark mb-2">Este Mes</h3>
          <p className="text-2xl font-bold text-green-600">{data?.quotes_month || 0} cotizaciones</p>
        </div>
      </div>

      {/* Recent Quotes */}
      <div className="bg-white rounded shadow">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-steel-dark uppercase">
            Cotizaciones Recientes
          </h2>
          <Link to="/admin/cotizaciones" className="text-accent-primary hover:text-accent-hover text-sm font-medium">
            Ver todas
          </Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Folio</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Productos</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.recent_quotes?.length > 0 ? (
                data.recent_quotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{quote.folio}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-steel-dark">{quote.cliente_nombre}</p>
                        <p className="text-xs text-steel-gray">{quote.cliente_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-steel-dark text-white text-xs px-2 py-1 rounded">
                        {quote.productos_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${statusColors[quote.estado] || statusColors.nueva}`}>
                        {statusLabels[quote.estado] || quote.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-steel-gray">
                      {new Date(quote.created_at).toLocaleDateString('es-MX')}
                    </td>
                    <td className="px-4 py-3">
                      <Link 
                        to={`/admin/cotizaciones/${quote.id}`}
                        className="text-accent-primary hover:text-accent-hover"
                      >
                        <Eye size={18} />
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-steel-gray">
                    No hay cotizaciones recientes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
